import { Suspense, useState, useEffect, useRef } from 'react'
import { useGLTF, useAnimations } from '@react-three/drei'
import { Color, Raycaster, Vector2, Vector3, Quaternion } from 'three'
import { useThree, useFrame } from '@react-three/fiber'
import './Model.css'
import { playObjectSound, playEventSound10 } from '../../utils/soundUtils'

const Model = ({ path, scale = 1, position = [0, 0, 0], rotation = [0, 0, 0], onHotspotClick, showContent, onObjectsLoaded }) => {
  const [selectedObject, setSelectedObject] = useState(null)
  const groupRef = useRef()
  const originalPositions = useRef({})
  const originalScales = useRef({})
  const targetScales = useRef({})
  const opacities = useRef({})
  const targetPositions = useRef({})
  const nodeRefs = useRef({})
  const raycaster = useRef(new Raycaster())
  const mouse = useRef(new Vector2())
  const { camera } = useThree()
  const [targetRotation, setTargetRotation] = useState(() => new Quaternion())
  const allObjectsRef = useRef([])




  useEffect(() => {
    if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
      window.createImageBitmap = undefined
    }
  }, [])

  const { scene, animations } = useGLTF(path, false)
  const { mixer, actions, names } = useAnimations(animations, scene)

  const setNodeColor = (node, color, opacity = 1) => {
    if (node && node.material) {
      node.material.color = color;
      node.material.transparent = opacity < 1;  // Nur transparent machen wenn nötig
      node.material.opacity = opacity;
      if (node.material.emissive) {
        node.material.emissive = color;
        node.material.emissiveIntensity = 0.01;  // Optional: Helligkeit der Emission

      }
    }
  }

  const setObjectOpacity = (object, opacity) => {
    if (object.material) {
      object.material.transparent = true
      object.material.opacity += (opacity - (object.material.opacity || 1)) * 0.1
    }
    if (object.children) {
      object.children.forEach(child => setObjectOpacity(child, opacity))
    }
  }

  useEffect(() => {
    if (!showContent) {
      const objects = scene.children.filter(child => child.name.includes('Object_'))
      objects.forEach(obj => {
        targetScales.current[obj.name]?.set(1.4, 1.4, 1.4)
        if (originalPositions.current[obj.name]) {
          targetPositions.current[obj.name].copy(originalPositions.current[obj.name])
        }
        opacities.current[obj.name] = 1
      })
      setSelectedObject(null)
    }
  }, [showContent])

  const handleObjectClick = (e) => {

    e.stopPropagation()

    const allObjects = scene.children.filter(child => child.name && child.name.includes('Object_'))
    allObjects.forEach(obj => {

      obj.traverse(child => {
        if (child.isMesh && nodeRefs.current[child.name]?.originalColor) {
          setNodeColor(child, nodeRefs.current[child.name].originalColor)
          child.material.emissive = nodeRefs.current[child.name].originalEmissive
        }
      })
    })

    if (selectedObject) return

    const clickedObject = e.object.parent

    console.log(clickedObject)

    playObjectSound(clickedObject.name)
    setSelectedObject(clickedObject)

    const rotationQuaternion = new Quaternion().setFromUnitVectors(
      new Vector3(0, 1.0409, 1.7),
      new Vector3(0, 1.0409, 1.7)
    )
    const targetQuaternion = rotationQuaternion.multiply(groupRef.current.quaternion)
    setTargetRotation(targetQuaternion)

    const selectedIndex = allObjects.findIndex(obj => obj === clickedObject)
    allObjects.forEach((obj, index) => {
      if (!originalPositions.current[obj.name]) {
        originalPositions.current[obj.name] = obj.position.clone()
        originalScales.current[obj.name] = obj.scale.clone()
      }
      targetPositions.current[obj.name] = new Vector3()
      targetScales.current[obj.name] = new Vector3()

      if (obj !== clickedObject) {
        const offset = (index - selectedIndex) * 10
        targetPositions.current[obj.name].set(offset, 0, 0)
        targetScales.current[obj.name].set(1, 1, 1)
        opacities.current[obj.name] = 0
      } else {
        targetPositions.current[obj.name].set(0, 0, 0)
        targetScales.current[obj.name].set(2, 2, 2)
        opacities.current[obj.name] = 1
      }
    })

    window.dispatchEvent(new Event('resetCameraTilt'))
    onHotspotClick?.(e.object.name)
  }

  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.quaternion.slerp(targetRotation, 0.05)
      Object.entries(targetPositions.current).forEach(([name, targetPos]) => {
        const obj = scene.getObjectByName(name)
        if (obj) {

          obj.position.lerp(targetPos, 0.15)
          if (name === 'Object_03') {
            obj.scale.lerp({ x: 1.7, y: 1.7, z: 1.7 }, 0.1)
          } else {
            obj.scale.lerp(targetScales.current[name], 0.1)
          }

          setObjectOpacity(obj, opacities.current[name])
        }
      })
    }
  })

  useEffect(() => {
    const allObjects = scene.children.filter(obj => obj.name?.includes('Object_'))
    allObjectsRef.current = allObjects
    onObjectsLoaded?.(allObjects)
    allObjects.forEach(obj => {
      if (!originalPositions.current[obj.name]) {
        originalPositions.current[obj.name] = obj.position.clone()
        targetPositions.current[obj.name] = obj.position.clone()
        targetScales.current[obj.name] = new Vector3(1, 1, 1)
        opacities.current[obj.name] = 1
      }
    })
  }, [scene])

  useEffect(() => {
    const handleReset = () => {
      const objects = scene.children.filter(child => child.name?.includes('Object_'))
      objects.forEach(obj => {
        if (originalPositions.current[obj.name]) {
          targetPositions.current[obj.name].copy(originalPositions.current[obj.name])
        }
        targetScales.current[obj.name]?.set(1.4, 1.4, 1.4)
        opacities.current[obj.name] = 1
      })
      setSelectedObject(null)
    }

    const handleSelect = ({ detail }) => {
      const obj = scene.getObjectByName(detail.objectName)
      if (!obj) return
      setSelectedObject(obj)
      onHotspotClick?.(obj.children[0].name)

      allObjectsRef.current.forEach((item, index) => {
        if (item.name === detail.objectName) {
          targetPositions.current[item.name].set(0, 0, 0)
          targetScales.current[item.name].set(1.5, 1.5, 1.5)
          opacities.current[item.name] = 1
        } else {
          const offset = (index > allObjectsRef.current.indexOf(obj) ? 1 : -1) * 10
          targetPositions.current[item.name].set(offset, 0, 0)
          targetScales.current[item.name].set(1, 1, 1)
          opacities.current[item.name] = 0
        }
      })
    }

    window.addEventListener('resetObjects', handleReset)
    window.addEventListener('selectObject', handleSelect)
    return () => {
      window.removeEventListener('resetObjects', handleReset)
      window.removeEventListener('selectObject', handleSelect)
    }
  }, [scene])

  useEffect(() => {

    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);


    if (isMobile) return;
    const handleMouseMove = (event) => {

      raycaster.current.setFromCamera(mouse.current, camera)
      const objects = scene.children.filter(obj => obj.name?.includes('Object_'))
      const intersects = raycaster.current.intersectObjects(objects, true)

      Object.entries(nodeRefs.current).forEach(([_, node]) => {
        if (node.originalColor) setNodeColor(node, node.originalColor)
        if (node.originalEmissive) node.material.emissive = node.originalEmissive
      })

      if (selectedObject) return
      const container = document.querySelector('.canvas-container')
      if (!container) return
      const rect = container.getBoundingClientRect()
      mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
      mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

      if (intersects.length > 0) {
        const obj = intersects[0].object
        const name = obj.name

        if (!nodeRefs.current[name]?.originalColor) {
          nodeRefs.current[name] = {
            originalColor: obj.material.color.clone(),
            originalEmissive: obj.material.emissive?.clone()
          }
        }
        setNodeColor(obj, new Color('blue'))
        Object.entries(nodeRefs.current).forEach(([n, node]) => {
          if (n !== name && node.originalColor) {
            setNodeColor(node, node.originalColor)
            if (node.originalEmissive) node.material.emissive = node.originalEmissive
          }
        })
      } else {
        Object.entries(nodeRefs.current).forEach(([_, node]) => {
          if (node.originalColor) setNodeColor(node, node.originalColor)
          if (node.originalEmissive) node.material.emissive = node.originalEmissive
        })
      }
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [camera, selectedObject])

  useEffect(() => {
    if (mixer && actions && names.length > 0) {
      const first = names[0]
      if (actions[first]) actions[first].play()
    }

    const storeNodes = (obj) => {
      if (obj.material && obj.name) {
        nodeRefs.current[obj.name] = obj
        nodeRefs.current[obj.name].originalColor = obj.material.color.clone()
        if (obj.material.emissive) {
          nodeRefs.current[obj.name].originalEmissive = obj.material.emissive.clone()
        }
      }
      if (obj.children) obj.children.forEach(storeNodes)
    }

    storeNodes(scene)
  }, [mixer, actions, names])

  return (
    <Suspense fallback={null}>
      <group
        ref={groupRef}
        scale={scale}
        position={position}
        rotation={rotation}
        onClick={(e) => e.stopPropagation()}>
        <primitive object={scene} onClick={handleObjectClick} />
      </group>
    </Suspense>
  )
}

export default Model